import React, { useEffect, useMemo, useState } from "react";
import { listCategories, listProducts } from "@/lib/api";
import { Search, ScanBarcode } from "lucide-react";
import { TID } from "@/constants/testIds";

export default function ProductGrid({ onPick, onBarcode }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listCategories(), listProducts({})]).then(([c, p]) => {
      setCategories(c);
      setProducts(p);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== "all") list = list.filter((p) => p.category_id === active);
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(qq) ||
          p.sku.toLowerCase().includes(qq) ||
          (p.barcode || "").includes(qq)
      );
    }
    return list;
  }, [products, active, q]);

  // Detección de código de barras (terminado en Enter)
  const handleKey = (e) => {
    if (e.key === "Enter" && q.length >= 8 && /^\d+$/.test(q)) {
      const match = products.find((p) => p.barcode === q);
      if (match) {
        onBarcode?.(match);
        setQ("");
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Search bar */}
      <div className="p-3 border-b border-slate-200 bg-white flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute top-3 left-3 text-slate-400" />
          <input
            data-testid={TID.posSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar producto, SKU o escanear código de barras…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 focus:border-slate-400 focus:outline-none text-sm"
          />
        </div>
        <div className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center gap-2 text-xs text-slate-500">
          <ScanBarcode size={16} /> Pistola lista
        </div>
      </div>

      {/* Categories */}
      <div className="px-3 py-2 border-b border-slate-200 bg-white flex gap-2 overflow-x-auto thin-scroll">
        <CategoryChip
          name="Todo"
          active={active === "all"}
          onClick={() => setActive("all")}
          color="#0F172A"
        />
        {categories.map((c) => (
          <CategoryChip
            key={c.id}
            tid={TID.posCategory(c.id)}
            name={c.name}
            active={active === c.id}
            onClick={() => setActive(c.id)}
            color={c.color}
          />
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto thin-scroll p-3 bg-slate-50">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-32 bg-white rounded-lg border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <div className="font-display text-base">Sin resultados</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((p) => (
              <button
                key={p.id}
                data-testid={TID.posProduct(p.id)}
                onClick={() => onPick(p)}
                className="product-card text-left p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-900 hover:shadow-md flex flex-col gap-1 min-h-[120px]"
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{p.sku}</div>
                <div className="font-medium text-slate-800 text-sm line-clamp-2 flex-1">{p.name}</div>
                <div className="flex items-end justify-between mt-1">
                  <div className="text-[11px] text-slate-500">
                    Stock: <span className={`font-mono ${p.stock <= 10 ? "text-red-600" : "text-slate-700"}`}>{p.stock}</span>
                  </div>
                  <div className="font-mono font-semibold text-slate-900">{p.price.toFixed(2)}€</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChip({ name, active, onClick, color, tid }) {
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`shrink-0 h-9 px-4 rounded-full text-sm font-medium transition-colors border ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      }`}
      style={active ? {} : { borderLeftWidth: 3, borderLeftColor: color }}
    >
      {name}
    </button>
  );
}
